import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Hero from '../components/ui/Hero';
import Section from '../components/ui/Section';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import NewsletterSignup from '../components/forms/NewsletterSignup';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCalendar, FiMapPin, FiClock, FiUsers, FiHeart, FiMusic, FiBook } = FiIcons;

const Events = () => {
  const [filter, setFilter] = useState('all');

  const events = [
    {
      id: 1,
      type: 'campus',
      title: 'UF Campus Outreach',
      date: '2024-01-15',
      time: '12:00 PM - 2:00 PM',
      location: 'University of Florida - Student Union Plaza',
      description: 'Join us for conversations with students during lunch hour. We\'ll be sharing the gospel and praying with anyone who wants prayer.',
      capacity: '10 volunteers needed',
      category: 'Campus Outreach'
    },
    {
      id: 2,
      type: 'city',
      title: 'Downtown Worship Night',
      date: '2024-01-18',
      time: '7:00 PM - 9:00 PM',
      location: 'Bo Diddley Plaza, Gainesville',
      description: 'An evening of worship and prayer in downtown Gainesville. Come join us as we create an atmosphere for God to move in people\'s hearts.',
      capacity: 'Open to all',
      category: 'City Outreach'
    },
    {
      id: 3,
      type: 'training',
      title: 'Evangelism Training Workshop',
      date: '2024-01-22',
      time: '6:00 PM - 8:00 PM',
      location: 'Love 180 Office, Gainesville',
      description: 'Learn practical skills for sharing your faith naturally and effectively. Perfect for new volunteers or anyone wanting to grow in evangelism.',
      capacity: '20 participants max',
      category: 'Training'
    },
    {
      id: 4,
      type: 'campus',
      title: 'FSU Bible Study Launch',
      date: '2024-01-25',
      time: '7:30 PM - 9:00 PM',
      location: 'Florida State University - Student Life Building',
      description: 'Starting a new Bible study group for FSU students. Come check it out if you\'re a student or want to support this new initiative.',
      capacity: 'Students welcome',
      category: 'Campus Outreach'
    },
    {
      id: 5,
      type: 'city',
      title: 'Orlando Street Evangelism',
      date: '2024-01-28',
      time: '10:00 AM - 1:00 PM',
      location: 'Downtown Orlando - Church Street Area',
      description: 'Taking the gospel to the streets of Orlando. We\'ll be engaging people in conversations and sharing the love of Christ.',
      capacity: '8 volunteers needed',
      category: 'City Outreach'
    },
    {
      id: 6,
      type: 'special',
      title: 'Monthly Prayer & Vision Night',
      date: '2024-02-01',
      time: '7:00 PM - 9:00 PM',
      location: 'Community Center, Gainesville',
      description: 'Join our team and supporters for a time of prayer, vision casting, and fellowship. Hear testimonies and learn about upcoming initiatives.',
      capacity: 'Open to supporters',
      category: 'Special Event'
    }
  ];

  const filteredEvents = filter === 'all' 
    ? events 
    : events.filter(e => e.type === filter);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long',
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getEventIcon = (type) => {
    switch (type) {
      case 'campus': return FiUsers;
      case 'city': return FiHeart;
      case 'training': return FiBook;
      case 'special': return FiMusic;
      default: return FiCalendar;
    }
  };

  return (
    <div>
      <Hero
        title="Upcoming Events"
        description="Join us for campus outreach, city evangelism, training workshops, and special events. Every event is an opportunity to see God transform lives and advance His kingdom in Florida."
        primaryCta={{
          text: 'Sign Up for Updates',
          href: '#newsletter'
        }}
        backgroundImage="https://images.unsplash.com/photo-1511578314322-379afb476865?ixlib=rb-4.0.3&auto=format&fit=crop&w=1950&q=80"
      />

      {/* Filter Navigation */}
      <Section padding="sm" background="gray">
        <div className="flex justify-center">
          <div className="bg-white rounded-lg p-1 shadow-sm">
            <div className="flex flex-wrap justify-center gap-1">
              <button
                onClick={() => setFilter('all')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  filter === 'all' 
                    ? 'bg-primary-600 text-white' 
                    : 'text-neutral-600 hover:text-primary-600'
                }`}
              >
                All Events
              </button>
              <button
                onClick={() => setFilter('campus')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  filter === 'campus' 
                    ? 'bg-primary-600 text-white' 
                    : 'text-neutral-600 hover:text-primary-600'
                }`}
              >
                Campus
              </button>
              <button
                onClick={() => setFilter('city')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  filter === 'city' 
                    ? 'bg-primary-600 text-white' 
                    : 'text-neutral-600 hover:text-primary-600'
                }`}
              >
                City Outreach
              </button>
              <button
                onClick={() => setFilter('training')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  filter === 'training' 
                    ? 'bg-primary-600 text-white' 
                    : 'text-neutral-600 hover:text-primary-600'
                }`}
              >
                Training
              </button>
              <button
                onClick={() => setFilter('special')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  filter === 'special' 
                    ? 'bg-primary-600 text-white' 
                    : 'text-neutral-600 hover:text-primary-600'
                }`}
              >
                Special Events
              </button>
            </div>
          </div>
        </div>
      </Section>

      {/* Events List */}
      <Section>
        <div className="space-y-6">
          {filteredEvents.map((event, index) => (
            <motion.div
              key={event.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <Card className="p-6">
                <div className="grid md:grid-cols-4 gap-6">
                  {/* Date & Time */}
                  <div className="md:col-span-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <SafeIcon icon={FiCalendar} className="w-5 h-5 text-primary-600" />
                      <span className="text-sm font-medium text-primary-600">{event.category}</span>
                    </div>
                    <div className="text-sm text-neutral-600 space-y-1">
                      <div>{formatDate(event.date)}</div>
                      <div className="flex items-center space-x-1">
                        <SafeIcon icon={FiClock} className="w-4 h-4" />
                        <span>{event.time}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <SafeIcon icon={FiMapPin} className="w-4 h-4" />
                        <span>{event.location}</span>
                      </div>
                    </div>
                  </div>

                  {/* Event Details */}
                  <div className="md:col-span-2">
                    <div className="flex items-center space-x-2 mb-3">
                      <SafeIcon icon={getEventIcon(event.type)} className="w-6 h-6 text-primary-600" />
                      <h3 className="text-xl font-semibold">{event.title}</h3>
                    </div>
                    <p className="text-neutral-600 mb-3">{event.description}</p>
                    <div className="text-sm text-primary-600 font-medium">
                      {event.capacity}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="md:col-span-1 flex flex-col justify-center">
                    <Button variant="primary" size="sm" className="w-full mb-2">
                      Sign Up
                    </Button>
                    <Button variant="outline" size="sm" className="w-full">
                      More Info
                    </Button>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>

        {filteredEvents.length === 0 && (
          <div className="text-center py-12">
            <SafeIcon icon={FiCalendar} className="w-12 h-12 text-neutral-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-neutral-600 mb-2">No events found</h3>
            <p className="text-neutral-500">Try adjusting your filter or check back later for new events.</p>
          </div>
        )}
      </Section>

      {/* Event Sign-up Form */}
      <Section background="gray">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-neutral-900 mb-4">
              Join an Event
            </h2>
            <p className="text-lg text-neutral-600">
              Ready to get involved? Fill out this form to sign up for any of our upcoming events.
            </p>
          </div>

          <Card className="p-8">
            <form className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    First Name *
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  required
                  className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Which event are you interested in? *
                </label>
                <select
                  required
                  className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">Select an event</option>
                  {events.map(event => (
                    <option key={event.id} value={event.id}>
                      {event.title} - {formatDate(event.date)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  How did you hear about us?
                </label>
                <select className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent">
                  <option value="">Select an option</option>
                  <option value="campus">Met team on campus</option>
                  <option value="social">Social media</option>
                  <option value="friend">Friend/Family</option>
                  <option value="church">Church partnership</option>
                  <option value="website">Website</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Additional Comments or Questions
                </label>
                <textarea
                  rows={3}
                  className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Let us know if you have any questions or special needs..."
                />
              </div>

              <Button type="submit" variant="primary" size="lg" className="w-full">
                Sign Up for Event
              </Button>
            </form>
          </Card>
        </div>
      </Section>

      {/* Newsletter Signup */}
      <Section id="newsletter">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-neutral-900 mb-4">
              Never Miss an Event
            </h2>
            <p className="text-lg text-neutral-600">
              Subscribe to our newsletter to get notified about upcoming events, outreach opportunities, and ministry updates.
            </p>
          </div>
          <NewsletterSignup />
        </div>
      </Section>
    </div>
  );
};

export default Events;